/* eslint-disable react/require-default-props */
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Dropdown, SplitButton, TableV8 } from "@xfrontend/xui-react-plus";
import HeaderSelectFilter from "#/modules/dashboard/components/HeaderSelectFilter";
import { FilterComponentProps, SelectOption } from "#/modules/dashboard/types";
import { UserItem, UserAssignment } from "#/apis/infra/users";
import { LicenseItem } from "#/apis/infra/listLicenses";
import { CustomerItem } from "#/apis/infra/customers";
import { Rbac } from "#/modules/auth/useRbac";
import { ROLE_NAMES } from "./RoleScopePicker";
import UserDetailPanel from "./UserDetailPanel";

// TanStack cell context (richer than the app's TableCellInfo: we need row.original).
type CellInfo = { row: { original: UserItem } };

const UserName = styled.div`
  color: var(--x-gray-200, #f6f6f7); font-weight: 600;
  .email { color: var(--x-gray-500, #a9a9ac); font-weight: 400; font-size: 11.5px; }
`;

// Role as plain table text (matches xcockpit's AccountList role column — no pill badge).
const RoleText = styled.span`color: var(--x-gray-200, #f6f6f7); font-size: 13px;`;

const Line = styled.div`margin-bottom: 3px; &:last-child { margin-bottom: 0; }`;
const ScopeChip = styled.span`color: var(--x-gray-400, #cfd0d3); font-size: 12px;`;
const Muted = styled.span`color: var(--x-gray-500, #a9a9ac); font-size: 12px;`;

// xcockpit Status tag: ACTIVE = teal (bg-info) + dark text, INACTIVE = grey (bg-secondary) + light.
const StatusBadge = styled.span<{ $on: boolean }>`
  display: inline-block; font-size: 10.5px; font-weight: 700; letter-spacing: .03em;
  text-transform: uppercase; border-radius: 4px; padding: 3px 9px;
  color: ${(p: { $on: boolean }) => (p.$on ? "var(--bs-dark, #1e2229)" : "var(--bs-white, #f6f6f7)")};
  background: ${(p: { $on: boolean }) => (p.$on ? "var(--bs-info, #34d7e6)" : "var(--bs-secondary, #5a5566)")};
`;

const Self = styled.span`color: var(--x-gray-500, #a9a9ac); font-size: 11px; font-style: italic;`;

// User status enum (xecguardcloud users_service._sync_user_cognito_status): "active" = enabled,
// "disable" = disabled. NB: users use "active" (licenses use "enable" — different field).
const isEnabled = (status: string): boolean => status !== "disable";

type UserTableProps = {
  users: UserItem[];
  loading: boolean;
  rbac: Rbac;
  // Raw scope data: orgs carry customer_id (license → parent territory), customers are the territories.
  orgs: LicenseItem[];
  customers: CustomerItem[];
  scopeName: (scopeType: string, scopeId: string) => string;
  // Org-card deep-link (?license=): pre-seed the Organization column filter to that org.
  initialOrgId?: string;
  onManageRoles: (user: UserItem) => void;
  onDisable: (user: UserItem) => void;
};

const UserTable = ({
  users, loading, rbac, orgs, customers, scopeName, initialOrgId = "", onManageRoles, onDisable,
}: UserTableProps): JSX.Element => {
  const { t } = useTranslation("management");
  // Column-header filters (xui TableV8 meta.filterComponent) — all searchable Select dropdowns.
  const [userFilter, setUserFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [territoryFilter, setTerritoryFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState(initialOrgId);
  const [statusFilter, setStatusFilter] = useState("");
  // Row double-click opens the slide-out detail; hold the id so it stays in sync with `users`.
  const [detailUserId, setDetailUserId] = useState<string | null>(null);

  // license_id → parent customer_id, plus id → display-name lookups for the two scope columns.
  const licToCustomer = useMemo(() => new Map(orgs.map((o) => [o.license_id, o.customer_id])), [orgs]);
  const licName = useMemo(() => new Map(orgs.map((o) => [o.license_id, o.license_name])), [orgs]);
  const cusName = useMemo(() => new Map(customers.map((c) => [c.customer_id, c.customer_name])), [customers]);

  // The Territory an assignment belongs to: CUSTOMER → itself, LICENSE → its parent, else "".
  const territoryIdOf = useCallback((a: UserAssignment): string => {
    if (a.scope_type === "CUSTOMER") return a.scope_id;
    if (a.scope_type === "LICENSE") return licToCustomer.get(a.scope_id) ?? "";
    return "";
  }, [licToCustomer]);

  const filtered = useMemo(() => users.filter((u) => {
    if (userFilter && u.user_id !== userFilter) return false;
    if (roleFilter && !u.assignments.some((a) => a.role === roleFilter)) return false;
    // Territory match cascades: a territory's own admins AND every user in its Organizations.
    if (territoryFilter && !u.assignments.some((a) => territoryIdOf(a) === territoryFilter)) return false;
    if (orgFilter && !u.assignments.some((a) => a.scope_type === "LICENSE" && a.scope_id === orgFilter)) {
      return false;
    }
    if (statusFilter === "enabled" && !isEnabled(u.status)) return false;
    if (statusFilter === "disabled" && isEnabled(u.status)) return false;
    return true;
  }), [users, userFilter, roleFilter, territoryFilter, orgFilter, statusFilter, territoryIdOf]);

  // Account filter: searchable Select of users (username can be blank → fall back to email; the
  // textValue carries both so type-ahead matches either).
  const userOptions: SelectOption[] = users.map((u) => ({
    label: u.username || u.email,
    value: u.user_id,
    textValue: `${u.username} ${u.email}`,
  }));
  const roleOptions: SelectOption[] = ROLE_NAMES.map((r) => ({ label: r, value: r, textValue: r }));
  const territoryOptions: SelectOption[] = customers.map((c) => ({
    label: c.customer_name, value: c.customer_id, textValue: c.customer_name,
  }));
  // Organization options narrow to the selected Territory — picking a Territory first collapses
  // this dropdown to just that territory's Organizations (and the filter remounts to clear stale).
  const orgOptions: SelectOption[] = orgs
    .filter((o) => !territoryFilter || licToCustomer.get(o.license_id) === territoryFilter)
    .map((o) => ({ label: o.license_name, value: o.license_id, textValue: o.license_name }));
  const statusOptions: SelectOption[] = [
    { label: t("status.enabled"), value: "enabled", textValue: t("status.enabled") },
    { label: t("status.disabled"), value: "disabled", textValue: t("status.disabled") },
  ];

  const columns = [
    {
      id: "user",
      accessorKey: "username",
      header: t("users.colUser"),
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={userOptions}
            onChange={(vals: string[]) => setUserFilter(vals[0] ?? "")}
          />
        ),
      },
      cell: (info: CellInfo) => {
        const u = info.row.original;
        return (
          <UserName>
            {u.username}
            <div className="email">{u.email}</div>
          </UserName>
        );
      },
    },
    {
      id: "role",
      accessorFn: (row: UserItem) => row.assignments.map((a) => a.role).join(", "),
      header: t("users.colRole"),
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={roleOptions}
            onChange={(vals: string[]) => setRoleFilter(vals[0] ?? "")}
          />
        ),
      },
      cell: (info: CellInfo) => {
        const u = info.row.original;
        if (u.assignments.length === 0) return <Muted>{t("users.noRole")}</Muted>;
        return (
          <>
            {u.assignments.map((a) => (
              <Line key={`${a.role}:${a.scope_type}:${a.scope_id}`}><RoleText>{a.role}</RoleText></Line>
            ))}
          </>
        );
      },
    },
    {
      id: "territory",
      accessorFn: (row: UserItem) => row.assignments.map((a) => territoryIdOf(a)).join(", "),
      header: t("scope.territory"),
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={territoryOptions}
            onChange={(vals: string[]) => { setTerritoryFilter(vals[0] ?? ""); setOrgFilter(""); }}
          />
        ),
      },
      cell: (info: CellInfo) => {
        const u = info.row.original;
        if (u.assignments.length === 0) return <Muted>—</Muted>;
        return (
          <>
            {u.assignments.map((a) => {
              const label = a.scope_type === "SYSTEM" ? "SYSTEM" : (cusName.get(territoryIdOf(a)) ?? "—");
              return <Line key={`${a.role}:${a.scope_type}:${a.scope_id}`}><ScopeChip>{label}</ScopeChip></Line>;
            })}
          </>
        );
      },
    },
    {
      id: "organization",
      accessorFn: (row: UserItem) => row.assignments
        .filter((a) => a.scope_type === "LICENSE").map((a) => a.scope_id).join(", "),
      header: t("scope.organization"),
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          // Controlled by orgFilter so it shows the deep-linked seed and clears on territory cascade.
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={orgOptions}
            selectedValues={orgFilter ? [orgFilter] : []}
            onChange={(vals: string[]) => setOrgFilter(vals[0] ?? "")}
          />
        ),
      },
      cell: (info: CellInfo) => {
        const u = info.row.original;
        if (u.assignments.length === 0) return <Muted>—</Muted>;
        return (
          <>
            {u.assignments.map((a) => (
              <Line key={`${a.role}:${a.scope_type}:${a.scope_id}`}>
                {a.scope_type === "LICENSE"
                  ? <ScopeChip>{licName.get(a.scope_id) ?? a.scope_id}</ScopeChip>
                  : <Muted>—</Muted>}
              </Line>
            ))}
          </>
        );
      },
    },
    {
      id: "status",
      accessorKey: "status",
      header: t("users.colStatus"),
      size: 170,
      meta: {
        filterComponent: (f: FilterComponentProps) => (
          <HeaderSelectFilter
            {...f}
            isMulti={false}
            options={statusOptions}
            onChange={(vals: string[]) => setStatusFilter(vals[0] ?? "")}
          />
        ),
      },
      cell: (info: CellInfo) => {
        const on = isEnabled(info.row.original.status);
        return <StatusBadge $on={on}>{on ? t("status.enabled") : t("status.disabled")}</StatusBadge>;
      },
    },
    {
      id: "actions",
      header: "",
      size: 180,
      cell: (info: CellInfo) => {
        const u = info.row.original;
        if (rbac.isSelf(u.user_id)) return <Self>{t("users.self")}</Self>;
        // xcockpit-style SplitButton: a primary action + a caret dropdown for the rest.
        return (
          <SplitButton
            btnVariant="secondary"
            dropdownVariant="secondary"
            size="sm"
            disabledMain={!rbac.canManageUsers}
            onClick={() => onManageRoles(u)}
            title={t("users.manageRoles")}
          >
            <Dropdown.Item disabled={!rbac.canDisableUser} onClick={() => onDisable(u)}>
              {t("users.disable")}
            </Dropdown.Item>
          </SplitButton>
        );
      },
    },
  ];

  return (
    <>
      <TableV8
        data={filtered}
        loading={loading}
        columns={columns}
        pagination={{ pageSize: 10 }}
        onDoubleClick={(event: MouseEvent, row: { original?: UserItem } | undefined) => {
          event.preventDefault();
          const o = row?.original;
          if (o) setDetailUserId(o.user_id);
        }}
      />
      <UserDetailPanel
        user={users.find((u) => u.user_id === detailUserId) ?? null}
        canManage={rbac.canManageUsers}
        scopeName={scopeName}
        onManageRoles={onManageRoles}
        onClose={() => setDetailUserId(null)}
      />
    </>
  );
};

export default UserTable;
